import React, {
    useEffect,
    useState,
} from 'react';
import {
    isEmpty,
    isFunction,
} from 'lodash';
import { useTranslation } from 'react-i18next';
import { BEM_BLOCK_NAME } from './constants';
import DownloadStatement from './downloadStatement';
import PaymentActivity from './paymentActivity';

type PropTypes = {
    /**
     * This prop should contain the user's access token,
     * to allow the Giving History component to make
     * authorized API calls to fetch the user's data.
     *
     * **_REQUIRED:_**  The user **_MUST_** be authenticated to view his or her Giving History.
     */
    accessToken: string;

    /**
     * Controls whether Giving Activity portion of the widget is enabled.
     *
     * If set to `false`, then only the Giving Statements portion is visible.
     *
     * If set to `true`, then Giving Activity portion is visible as well.
     */
    enableGivingActivity?: boolean;

    /**
     * The locale (language/culture) to render the widget in, e.g. `en-US`, `es`, `zh-Hant`.
     * Locales without translation resources fall back to English.
     *
     * If omitted, the current i18next language is left untouched (i18next is a singleton shared
     * with the other giving widgets on the page, so an absent locale must not reset their language).
     */
    locale?: string;

    /**
     * Controls how many years of Giving History should be included.
     *
     * If set to 1, only includes the current year to date.
     *
     * If set to 2, includes the current year and previous year.
     *
     * If set to 3, includes the current year and previous two years.
     *
     * etc.
     */
    numberOfYearsToInclude?: number;

    /**
     * Boolean flag controlling whether or not to show the Excel Spreadsheet button for exporting the Giving History
     * data to an Excel spreadsheet (XLSX file).
     *
     * Optional; defaults to **`false`** (don't show the Excel Spreadsheet button).
     */
    showExcelDownloadButton?: boolean;

    /**
     * Boolean flag controlling whether or not to show the Search and Filtering controls for Giving History.
     *
     * Optional; defaults to **`false`** (don't show the Search and Filtering controls).
     */
    showSearchAndFilterControls?: boolean;

    /**
     * Boolean flag controlling whether or not to show the summary statistics on the Giving History data
     * (e.g. This Month, Year to Date, Previous Year to Date, Previous Year Total).
     *
     * Optional; defaults to **`false`** (don't show the statistics).
     */
    showStatistics?: boolean;

    /**
     * Boolean flag controlling whether or not to use Rock RMS compatible (v4) API endpoints.
     */
    useRockApis?: boolean;
};

/**
 * This is the main shared component (used by both the public page and widget)
 * for Giving History
 */
function GivingHistory({
    accessToken,
    enableGivingActivity = false,
    locale,
    numberOfYearsToInclude = 1,
    showExcelDownloadButton = false,
    showSearchAndFilterControls = false,
    showStatistics = false,
    useRockApis = false,
}: PropTypes) {
    const { i18n } = useTranslation();
    const [includeSpouseGiving, setIncludeSpouseGiving] = useState<boolean>(true);

    useEffect(() => {
        console.log('[Giving History Widget] Using Rock APIs', useRockApis); // eslint-disable-line no-console
    }, [useRockApis]);

    // See if we need to change the locale (mirrors the Campaign Commitment widget's i18n handling)
    useEffect(() => {
        if (!isEmpty(locale) &&
            isFunction(i18n.changeLanguage) &&
            locale !== i18n.language) {
            // eslint-disable-next-line no-console
            console.log('Healthy Church Giving History Widget: i18n - Changing language to:', locale);
            i18n.changeLanguage(locale);
        }
    }, [
        i18n,
        locale,
    ]);

    return (
        <div className={BEM_BLOCK_NAME}>
            <DownloadStatement
                accessToken={accessToken}
                includeSpouseGiving={includeSpouseGiving}
                onChangeIncludeSpouseGiving={setIncludeSpouseGiving}
                useRockAPIs={useRockApis}
            />

            <PaymentActivity
                accessToken={accessToken}
                enabled={enableGivingActivity}
                includeSpouseGiving={includeSpouseGiving}
                numberOfYearsToInclude={numberOfYearsToInclude}
                showExcelDownloadButton={showExcelDownloadButton}
                showSearchAndFilterControls={showSearchAndFilterControls}
                showStatistics={showStatistics}
                useRockAPIs={useRockApis}
            />
        </div>
    );
}

export default GivingHistory;
